import axios from 'axios'
import { normalizePhoneAvailability } from '@core/utils/time'
import { resolvePhoneTimezone } from '@/views/call/phoneTimezone'
import { isValidPhoneNumber, normalizePhoneNumber } from '@/views/call/phoneNumber'
import { functionKeyHeaders, unloadSafePost } from '@core/utils/functionsApi'

const loadedUserData = JSON.parse(localStorage.getItem('userData'))
const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

const createDefaultPhoneAvailability = () => ({
  availability: daysOfWeek.map(day => ({
    day,
    slots: [],
  })),
})

const getDeviceTwin = async equipment => {
  const response = await axios.get(
    `${process.env.VUE_APP_FUNCTION_BASE_URL}/proxy/ping`,
    {
      headers: functionKeyHeaders(process.env.VUE_APP_FUNCTION_PROXY_KEY),
      params: {
        equipments: equipment,
      },
    },
  )
  return {
    agentAvailable: response.data[0].properties?.desired?.agentAvailable,
    phoneCallEnabled: response.data[0].properties?.desired?.enablePhoneCall,
    phoneAvailability: response.data[0].properties?.desired?.phoneAvailability?.availability || [],
    phoneNumber: response.data[0].properties?.desired?.phoneAvailability?.phone || null,
    timeZone: response.data[0].properties?.desired?.phoneAvailability?.timezone || null,
  }
}

const state = {
  // THIS agent's own availability (toggle / busy-on-call). Drives whether this dashboard rings.
  // The device twin's agentAvailable is a different thing (the backend-derived "is ANY agent
  // available" aggregate) and deliberately does NOT feed this value.
  agentAvailable: false,
  phoneCallEnabled: false,
  phoneAvailability: createDefaultPhoneAvailability().availability,
  phoneNumber: null,
  timeZone: null,
}

const mutations = {
  SET_AGENT_AVAILABLE(localState, value) {
    localState.agentAvailable = value
  },
  SET_PHONE_CALL_ENABLED(localState, value) {
    localState.phoneCallEnabled = value
  },
  SET_PHONE_AVAILABILITY(localState, value) {
    localState.phoneAvailability = normalizePhoneAvailability(value)
  },
  UPDATE_PHONE_AVAILABILITY(localState, { day, slots }) {
    const dayAvailability = localState.phoneAvailability.find(item => item.day === day)
    const normalizedSlots = normalizePhoneAvailability([{ day, slots }])[0]?.slots || []

    if (!dayAvailability) {
      localState.phoneAvailability.push({
        day,
        slots: normalizedSlots,
      })
      return
    }
    dayAvailability.slots = normalizedSlots
  },
  // Whatever reaches the state (agent input, legacy twin value) is stored in E.164 or not at all,
  // so every body built from the state carries a number the backend can dial.
  UPDATE_PHONE_NUMBER(localState, value) {
    localState.phoneNumber = normalizePhoneNumber(value)
  },
  // Whatever the twin holds (legacy UTC offset, unset) is normalised to an offered IANA zone, so
  // the next phone-settings save writes the corrected value back to the twin.
  UPDATE_TIME_ZONE(localState, value) {
    localState.timeZone = resolvePhoneTimezone(value)
  },
}

const actions = {
  async fetchDeviceTwin({ rootState, commit }, equipmentSerial = null) {
    const equipment = equipmentSerial || rootState.equipment.equipments[0]?.serialNumber
    if (!equipment) {
      return false
    }

    const twin = await getDeviceTwin(equipment)

    // twin.agentAvailable is deliberately ignored: it is the fleet aggregate ("any agent
    // available"), not this agent's own toggle, so it must never overwrite it.
    commit('SET_PHONE_CALL_ENABLED', twin.phoneCallEnabled)
    commit('SET_PHONE_AVAILABILITY', createDefaultPhoneAvailability().availability)
    twin.phoneAvailability.forEach(dayAvailability => {
      commit('UPDATE_PHONE_AVAILABILITY', {
        day: dayAvailability.day,
        slots: dayAvailability.slots,
      })
    })
    commit('UPDATE_PHONE_NUMBER', twin.phoneNumber)
    commit('UPDATE_TIME_ZONE', twin.timeZone)
    return true
  },

  async toggleAgentAvailable({ commit, state: localState, dispatch }) {
    const previousValue = localState.agentAvailable
    const newValue = !localState.agentAvailable
    commit('SET_AGENT_AVAILABLE', newValue)
    const success = await dispatch('updateDeviceTwin', { agentAvailable: newValue })
    if (!success) {
      commit('SET_AGENT_AVAILABLE', previousValue)
    }
    return success
  },
  async updatePhoneAvailability({ commit, state: localState, dispatch }, { day, slots }) {
    const previousAvailability = localState.phoneAvailability.find(item => item.day === day)?.slots || []
    commit('UPDATE_PHONE_AVAILABILITY', { day, slots })
    const success = await dispatch('updateDeviceTwin', { phoneAvailability: localState.phoneAvailability })
    if (!success) {
      commit('UPDATE_PHONE_AVAILABILITY', { day, slots: previousAvailability })
    }
    return success
  },
  async updatePhoneNumber({ commit, state: localState, dispatch }, phoneNumber) {
    // A keystroke that is not yet a number must never erase the one that answers calls today.
    if (!isValidPhoneNumber(phoneNumber)) {
      return false
    }
    const previousPhoneNumber = localState.phoneNumber
    commit('UPDATE_PHONE_NUMBER', phoneNumber)
    const success = await dispatch('updateDeviceTwin', { phoneNumber })
    if (!success) {
      commit('UPDATE_PHONE_NUMBER', previousPhoneNumber)
    }
    return success
  },
  async updatePhoneCallEnabled({ commit, state: localState, dispatch }, value) {
    const previousValue = localState.phoneCallEnabled
    commit('SET_PHONE_CALL_ENABLED', value)
    const success = await dispatch('updateDeviceTwin', { phoneCallEnabled: value })
    if (!success) {
      commit('SET_PHONE_CALL_ENABLED', previousValue)
    }
    return success
  },
  updateTimeZone({ commit, state: localState, dispatch }, timeZone) {
    const previousTimeZone = localState.timeZone
    commit('UPDATE_TIME_ZONE', timeZone)
    dispatch('updateDeviceTwin', { timeZone })
      .catch(() => {
        commit('UPDATE_TIME_ZONE', previousTimeZone)
      })
  },
  async updateDeviceTwin({ rootState }, payload = {}) {
    // Turning transfer ON without a dialable number sends every visitor to a call nobody takes.
    // Turning it OFF stays possible whatever the number holds.
    if (payload.phoneCallEnabled === true && !isValidPhoneNumber(rootState.agent.phoneNumber)) {
      return false
    }
    const {
      agentAvailable = null,
      phoneCallEnabled = null,
      phoneAvailability = null,
      phoneNumber = null,
      timeZone = null,
    } = payload

    if (agentAvailable === null && phoneCallEnabled === null && phoneAvailability === null && phoneNumber === null && timeZone === null) {
      return true // No update needed
    }
    const userData = JSON.parse(localStorage.getItem('userData')) || loadedUserData
    const data = {}
    const phoneAvailabilityData = {
      availability: normalizePhoneAvailability(rootState.agent.phoneAvailability),
      phone: rootState.agent.phoneNumber,
      timezone: rootState.agent.timeZone,
    }
    if (agentAvailable != null) {
      data.agentAvailable = agentAvailable
    } else if (phoneCallEnabled != null) {
      data.enablePhoneCall = phoneCallEnabled
    } else {
      if (phoneAvailability != null) {
        phoneAvailabilityData.availability = normalizePhoneAvailability(phoneAvailability)
      } else if (phoneNumber != null) {
        phoneAvailabilityData.availability = normalizePhoneAvailability(rootState.agent.phoneAvailability)
      } else if (timeZone != null) {
        phoneAvailabilityData.timezone = timeZone
      }
      data.phoneAvailability = phoneAvailabilityData
    }
    // Identity goes in the body, never the query string: an email address in a URL is stored in
    // browser history and server logs, and ad blockers reject such requests (ERR_BLOCKED_BY_CLIENT).
    data.username = userData.username
    data.client = userData.clientName
    const equipments = rootState.equipment.equipments.map(item => item.serialNumber).join(',')
    const url = `${process.env.VUE_APP_FUNCTION_BASE_URL}/proxy/update?equipments=${equipments}`
    const maxAttempts = 3
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        await axios.post(url, data, {
          headers: functionKeyHeaders(process.env.VUE_APP_FUNCTION_PROXY_UPDATE_KEY),
        })
        return true // Success
      } catch (error) {
        if (attempt === maxAttempts) {
          return false // Failed after max attempts
        }
      }
    }
    return false // Return false if all attempts fail
  },
  // Fire-and-forget availability update. Its most important caller is the `beforeunload` handler
  // that marks this agent unavailable when the tab closes, which gets a single chance to reach the
  // server — so this goes out as a preflight-free "simple" request. See unloadSafePost.
  updateDeviceTwinSync({ rootState }, value) {
    const userData = JSON.parse(localStorage.getItem('userData')) || loadedUserData
    // Identity in the body, not the query string (history/logs + ad-blocker filters).
    const payload = JSON.stringify({
      agentAvailable: value,
      username: userData.username,
      client: userData.clientName,
    })
    const equipments = rootState.equipment.equipments.map(item => item.serialNumber).join(',')
    const { url, init } = unloadSafePost(
      `${process.env.VUE_APP_FUNCTION_BASE_URL}/proxy/update?equipments=${equipments}`,
      process.env.VUE_APP_FUNCTION_PROXY_UPDATE_KEY,
      payload,
    )

    return fetch(url, init)
      .then(response => response.ok)
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error('Failed to sync agent availability to the device twin.', error)
        return false
      })
  },
}

const getters = {
  agentAvailable: localState => localState.agentAvailable,
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
